import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
  signal,
  inject,
  Injector,
  afterNextRender,
  viewChild,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';

import { CanvasBodyPortalService } from '../../../canvas-body-portal.service';
import { CANVAS_NOTIFIER } from '../../../../canvas-tokens';
import { CanvasToolbarIconComponent } from '../../../icons/canvas-toolbar-icon.component';
import {
  exportCanvasLayersAsGeoJsonZip,
  exportMapAsJpegDataUrl,
} from '../../canvas-map-ol.utils';
import type { CanvasExportLayerDescriptor } from '../../canvas-maps.models';

@Component({
  selector: 'lib-canvas-export-tool',
  standalone: true,
  imports: [CommonModule, CanvasToolbarIconComponent],
  template: `
    <button class="cv-tb__btn" (click)="open()" title="Export map / features" type="button">
      <lib-canvas-toolbar-icon name="export"></lib-canvas-toolbar-icon>
    </button>

    <div #exportPortal class="cv-body-portal" *ngIf="dialogOpen()">
      <div class="cv-import-dialog-backdrop" (click)="close()">
        <div class="cv-export-dialog" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
          <div class="cv-export-dialog__leaf" aria-hidden="true">
            <img src="/assets-leaf.png" alt="" />
          </div>

          <div class="cv-export-dialog__icon">
            <svg viewBox="0 0 24 24" fill="none" style="stroke: var(--canvas-action)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </div>

          <h2 class="cv-export-dialog__title">Export</h2>

          <div class="cv-export-dialog__form">
            <label class="cv-export-dialog__field">
              <span class="cv-export-dialog__field-label">
                Name
                <abbr class="cv-export-dialog__req" title="Required">*</abbr>
              </span>
              <input
                type="text"
                [value]="fileNameStr"
                (input)="onName($event)"
                placeholder="Enter name to be saved as"
                autocomplete="off"
              />
            </label>
            <div class="cv-export-dialog__format" role="radiogroup" aria-label="Export format">
              <span class="cv-export-dialog__format-label">Export as</span>
              <div class="cv-export-dialog__pills">
                <button
                  type="button"
                  role="radio"
                  class="cv-export-dialog__pill"
                  [class.cv-export-dialog__pill--active]="exportKind() === 'jpeg'"
                  [attr.aria-checked]="exportKind() === 'jpeg'"
                  (click)="setKind('jpeg')"
                >JPEG</button>
                <button
                  type="button"
                  role="radio"
                  class="cv-export-dialog__pill"
                  [class.cv-export-dialog__pill--active]="exportKind() === 'geojson'"
                  [attr.aria-checked]="exportKind() === 'geojson'"
                  (click)="setKind('geojson')"
                >GeoJSON</button>
              </div>
              <div class="cv-export-dialog__pill-hint-wrap">
                <p *ngIf="exportKind() === 'jpeg'" class="cv-export-dialog__pill-hint">
                  Map image only. Cannot be re-imported as layers—use for reference or sharing a snapshot.
                </p>
                <p *ngIf="exportKind() === 'geojson'" class="cv-export-dialog__pill-hint">
                  Saves visible map layers as GeoJSON in a zip file you can import back to restore or merge layers.
                </p>
              </div>
            </div>
          </div>

          <div class="cv-export-dialog__actions">
            <button type="button" class="cv-export-dialog__btn cv-export-dialog__btn--cancel" (click)="close()">
              Cancel
            </button>
            <button
              type="button"
              class="cv-export-dialog__btn cv-export-dialog__btn--export"
              [disabled]="!canSubmitExport()"
              [attr.title]="canSubmitExport() ? null : 'Enter a name to export'"
              (click)="runExport()"
            >
              Export
            </button>
          </div>
        </div>
      </div>
    </div>
    <a #downloadAnchor class="cv-tb-export-anchor" tabindex="-1"></a>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class ExportToolComponent implements OnDestroy {
  @Input({ required: true }) map!: OlMap;
  @Input({ required: true }) userDrawSource!: VectorSource;
  /** Visible vector layers from the layers panel (drawings + plotted datasets). */
  @Input() exportLayers: () => readonly CanvasExportLayerDescriptor[] = () => [];

  @ViewChild('downloadAnchor') private downloadAnchor!: ElementRef<HTMLAnchorElement>;

  private readonly injector = inject(Injector);
  private readonly bodyPortal = inject(CanvasBodyPortalService);
  private readonly notifier = inject(CANVAS_NOTIFIER, { optional: true });
  private readonly exportPortalRoot = viewChild<ElementRef<HTMLElement>>('exportPortal');

  private portalDetach?: () => void;

  readonly dialogOpen = signal(false);
  readonly exportKind = signal<'jpeg' | 'geojson'>('jpeg');
  fileNameStr = '';

  open(): void {
    this.dialogOpen.set(true);
    afterNextRender(
      () => {
        const root = this.exportPortalRoot()?.nativeElement;
        if (!root || !this.dialogOpen()) return;
        this.portalDetach?.();
        this.portalDetach = this.bodyPortal.attach(root);
      },
      { injector: this.injector },
    );
  }

  close(): void {
    this.portalDetach?.();
    this.portalDetach = undefined;
    this.dialogOpen.set(false);
    this.fileNameStr = '';
    this.exportKind.set('jpeg');
  }

  ngOnDestroy(): void {
    this.portalDetach?.();
    this.portalDetach = undefined;
  }

  onName(ev: Event): void {
    this.fileNameStr = (ev.target as HTMLInputElement).value;
  }

  canSubmitExport(): boolean {
    return this.fileNameStr.trim().length > 0;
  }

  setKind(kind: 'jpeg' | 'geojson'): void {
    this.exportKind.set(kind);
  }

  async runExport(): Promise<void> {
    if (!this.canSubmitExport()) return;

    const anchor = this.downloadAnchor?.nativeElement;
    if (!anchor) return;
    const name = this.fileNameStr.trim().replace(/[/\\?%*:|"<>]/g, '-');
    try {
      if (this.exportKind() === 'jpeg') {
        const dataUrl = await exportMapAsJpegDataUrl(this.map);
        anchor.href = dataUrl;
        anchor.download = `${name}.jpeg`;
        anchor.click();
      } else {
        const layers = this.exportLayers();
        const blob = exportCanvasLayersAsGeoJsonZip(layers);
        if (!blob) {
          this.notifier?.error('Nothing to export. Add or show layers with map features first.');
          return;
        }
        const url = URL.createObjectURL(blob);
        anchor.href = url;
        anchor.download = `${name}.zip`;
        anchor.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (e) {
      console.error('Export failed', e);
      this.notifier?.error('Export failed. Please try again.');
      return;
    }
    this.close();
  }
}
